import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/Modal";
import PlatformGlyph from "@/components/ui/PlatformGlyph";
import { APP_PLATFORM_LABEL, cn } from "@/lib/utils";
import { effectiveMode, systemModeLabel } from "@/lib/systemMode";
import type { AppSummaryOut } from "@/types";

/**
 * Login App-Selection Modal.
 *
 * Source: BRIEF_ia-redesign-frontend-implementation § 6;
 * docs/product/experience-and-ui.md § 1b App-Selection Modal.
 *
 * PO-8 Option B trigger logic + PO-9 post-modal landing rule.
 */

interface LoginAppSelectionModalProps {
  open: boolean;
  apps: AppSummaryOut[];
  lastSelectedAppId: string | null;
  onSelect: (appId: string) => void;
  onAddNew: () => void;
  userDisplayName?: string | null;
}

export default function LoginAppSelectionModal({
  open,
  apps,
  lastSelectedAppId,
  onSelect,
  onAddNew,
  userDisplayName,
}: LoginAppSelectionModalProps) {
  const { t } = useTranslation();
  // Sort: most-recently-used first (last_selected_at desc; nulls last).
  // The last-selected app appears top-left.
  const sortedApps = [...apps].sort((a, b) => {
    if (a.app_id === lastSelectedAppId) return -1;
    if (b.app_id === lastSelectedAppId) return 1;
    const aTime = a.last_selected_at
      ? new Date(a.last_selected_at).getTime()
      : 0;
    const bTime = b.last_selected_at
      ? new Date(b.last_selected_at).getTime()
      : 0;
    return bTime - aTime;
  });

  // Pinned literal — e2e:smoke.spec.ts asserts
  // getByRole("heading", { name: "Welcome back" }). Keep "Welcome back"
  // verbatim in source; do not extract to t().
  const greeting = userDisplayName
    ? `Welcome back, ${userDisplayName}`
    : "Welcome back";

  return (
    <Modal
      open={open}
      onClose={() => {}}
      title={greeting}
      disableClose
      className="max-w-3xl"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          {t("auth.loginAppSelectionModal.chooseAnAppToWorkOn")}
        </p>
        <div
          role="list"
          aria-label={t("auth.loginAppSelectionModal.appsAriaLabel")}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {sortedApps.map((app) => (
            <AppCard
              key={app.app_id}
              app={app}
              onClick={() => onSelect(app.app_id)}
            />
          ))}
          <AddNewAppCard onClick={onAddNew} />
        </div>
      </div>
    </Modal>
  );
}

interface AppCardProps {
  app: AppSummaryOut;
  onClick: () => void;
}

function AppCard({ app, onClick }: AppCardProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="listitem"
      onClick={onClick}
      className={cn(
        "group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-shadow",
        "hover:border-primary-400 hover:shadow-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
      )}
    >
      <div className="flex items-center gap-3">
        {app.icon_url ? (
          <img
            src={app.icon_url}
            alt=""
            className="size-12 rounded-lg"
            aria-hidden="true"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex size-12 items-center justify-center rounded-lg bg-gray-100 text-base font-semibold text-gray-600"
          >
            {app.brand_label
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part.charAt(0).toUpperCase())
              .join("") || "?"}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-gray-900">
            {app.brand_label}
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
            <PlatformGlyph platform={app.platform} />
            {APP_PLATFORM_LABEL[app.platform]}
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs text-gray-600">
        <div>
          <dt className="font-medium text-gray-500">
            {t("auth.loginAppSelectionModal.mode")}
          </dt>
          <dd className="font-semibold text-gray-900">
            {
              systemModeLabel[
                effectiveMode(app.system_mode, app.operator_mode_override)
              ]
            }
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-500">
            {t("auth.loginAppSelectionModal.running")}
          </dt>
          <dd className="font-semibold text-gray-900">
            {app.running_experiment_count}{" "}
            {app.running_console_count > 0 && (
              // ENG-RUNNING-COUNTS-STORE-FACT: the console share, named.
              <span className="ml-1 text-xs font-normal text-gray-500">
                {t("auth.loginAppSelectionModal.consoleShare", {
                  count: app.running_console_count,
                })}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </button>
  );
}

interface AddNewAppCardProps {
  onClick: () => void;
}

function AddNewAppCard({ onClick }: AddNewAppCardProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="listitem"
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 p-4 text-primary-text transition-colors",
        "hover:border-primary-500 hover:bg-primary-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
      )}
    >
      <Plus size={24} aria-hidden="true" />
      <span className="text-sm font-medium">
        {t("auth.loginAppSelectionModal.addNewApp")}
      </span>
    </button>
  );
}
